import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';

import WizardPage from './WizardPage';
import {
  Button,
  Modal,
  ProgressIndicator
} from '@salesforce/design-system-react';

import './Wizard.scss';

class Wizard extends Component {
  goPrevStep(currentPage) {
    const currentIndex = this.props.children.indexOf(currentPage);
    const nextIndex = currentIndex <= 0 ? 0 : currentIndex - 1;
    const nextPage = this.props.children[nextIndex];
    if (this.props.onStepChange) {
      if (!this.props.onStepChange(currentPage, nextPage)) {
        return;
      }
    }
    this.props.actions.updateModal(this.props.modalName, {
      currentStep: nextPage.props.step
    });
  }

  goNextStep(currentPage) {
    const currentIndex = this.props.children.indexOf(currentPage);
    const nextIndex =
      currentIndex >= this.props.children.length - 1
        ? this.props.children.length - 1
        : currentIndex + 1;
    const nextPage = this.props.children[nextIndex];
    if (this.props.onStepChange) {
      if (!this.props.onStepChange(currentPage, nextPage)) {
        return;
      }
    }
    this.props.actions.updateModal(this.props.modalName, {
      currentStep: nextPage.props.step
    });
  }

  goToStep(event, selection) {
    const nextStep = selection.step.step;
    const nextPage = this.props.children.find(
      page => page.props.step === nextStep
    );
    if (this.props.onStepChange) {
      if (!this.props.onStepChange(this.currentPage(), nextPage)) {
        return;
      }
    }
    this.props.actions.updateModal(this.props.modalName, {
      currentStep: nextStep
    });
  }

  onSaveExit() {
    (this.props.onSave || this.props.actions.closeModal)(this.props.modalName);
  }

  onFinish() {
    (this.props.onFinish || this.props.actions.closeModal)(
      this.props.modalName
    );
  }

  resolveProp(currentPage, propName, defaultValue = undefined) {
    let value = defaultValue;
    if (currentPage && currentPage.props[propName]) {
      value = currentPage.props[propName];
    }
    return value;
  }

  isCompleted(step) {
    const modal = this.props.modals[this.props.modalName];
    return (
      modal && modal.data && modal.data[step] && modal.data[step].completed
    );
  }

  currentStep() {
    const { children, modals, modalName } = this.props;
    return (
      (modals[modalName] &&
        modals[modalName].data &&
        modals[modalName].data.currentStep) ||
      children[0].props.step
    );
  }

  currentPage() {
    return this.props.children.find(
      child => child.props.step === this.currentStep()
    );
  }

  resolveFooter(currentPage, navigationDisabled, saveDisabled, finishDisabled) {
    const { formatMessage } = this.props.intl;
    const footer = [];

    if (currentPage) {
      const currentDisabled =
        currentPage.props.required && !this.isCompleted(currentPage.props.step);

      if (currentPage.props.footer.includes('CANCEL')) {
        footer.push(
          <Button
            key="wizardCancel"
            label={formatMessage({ id: 'common.actions.cancel' })}
            variant="neutral"
            onClick={this.close.bind(this)}
          />
        );
      }
      if (currentPage.props.footer.includes('BACK')) {
        const backLabel =
          this.props.backLabel || formatMessage({ id: 'common.actions.back' });
        footer.push(
          <Button
            key="wizardBack"
            label={backLabel}
            variant="neutral"
            disabled={navigationDisabled}
            onClick={this.goPrevStep.bind(this, currentPage)}
          />
        );
      }

      if (currentPage.props.footer.includes('PROGRESS')) {
        const progressSteps = this.props.children.filter(
          child => !child.props.excludeFromProgress
        );
        const steps = progressSteps.map((child, index) => ({
          id: index,
          label: child.props.stepName
            ? formatMessage({ id: child.props.stepName })
            : child.props.step,
          step: child.props.step,
          completed: child.props.required && this.isCompleted(child.props.step),
          disabled: child.props.required && !this.isCompleted(child.props.step)
        }));
        const currentIndex = progressSteps.indexOf(currentPage);

        // Show every step before the current step as completed
        const completedSteps = [
          ...steps.filter(child => steps.indexOf(child) < currentIndex)
        ];

        // Every step after a disabled step must also be disabled on the progress bar nav
        let disabledSteps = [];
        const disabledChildren = steps.filter(child => child.disabled);
        if (disabledChildren.length > 0) {
          disabledSteps = [
            ...steps.filter(
              child => steps.indexOf(child) > steps.indexOf(disabledChildren[0])
            )
          ];
        }
        footer.push(
          <ProgressIndicator
            key="progressIndicator"
            variant="modal"
            steps={steps}
            completedSteps={completedSteps}
            disabledSteps={navigationDisabled ? steps : disabledSteps}
            selectedStep={steps[currentIndex]}
            onStepClick={this.goToStep.bind(this)}
          />
        );
      }

      if (currentPage.props.footer.includes('SAVE')) {
        const saveLabel =
          this.props.saveLabel || formatMessage({ id: 'common.actions.save' });
        footer.push(
          <Button
            className="wizard-save-btn"
            key="wizardSave"
            label={saveLabel}
            variant="brand"
            disabled={navigationDisabled || currentDisabled || saveDisabled}
            onClick={this.onSaveExit.bind(this)}
          />
        );
      }

      if (currentPage.props.footer.includes('NEXT')) {
        const nextLabel =
          this.props.nextLabel || formatMessage({ id: 'common.actions.next' });
        footer.push(
          <Button
            className="wizard-next-btn"
            key="wizardNext"
            label={nextLabel}
            variant="brand"
            disabled={navigationDisabled || currentDisabled}
            onClick={this.goNextStep.bind(this, currentPage)}
          />
        );
      }

      if (currentPage.props.footer.includes('FINISH')) {
        const finishLabel =
          this.props.finishLabel ||
          formatMessage({ id: 'common.actions.finish' });
        footer.push(
          <Button
            className="wizard-next-btn"
            key="wizardFinish"
            label={finishLabel}
            variant="brand"
            disabled={navigationDisabled || currentDisabled || finishDisabled}
            onClick={this.onFinish.bind(this)}
          />
        );
      }
    }
    return footer;
  }

  close() {
    const { modalName, onClose } = this.props;
    this.props.actions.closeModal(modalName);
    if (onClose) {
      onClose();
    }
  }

  getClassName(containerClassName, flex) {
    const classes = [];
    if (containerClassName) {
      classes.push(containerClassName);
    }
    if (flex) {
      classes.push('wizard-footer-flex');
    }
    return classes.length > 0 ? classes.join(' ') : null;
  }

  render() {
    const { modals, modalName } = this.props;
    if (!modals[modalName]) {
      return null;
    }
    const isOpen =
      this.props.modals[modalName] && this.props.modals[modalName].isOpen;
    const navigationDisabled =
      this.props.modals[modalName] &&
      this.props.modals[modalName].navigationDisabled;
    const saveDisabled =
      this.props.modals[modalName] &&
      this.props.modals[modalName].data?.saveDisabled;
    const finishDisabled =
      this.props.modals[modalName] &&
      this.props.modals[modalName].data?.finishDisabled;
    const currentPage = this.currentPage();

    const { title, containerClassName, ...otherProps } = this.props;
    const modalClassName = this.getClassName(
      containerClassName,
      currentPage && currentPage.props.flex
    );
    return (
      <Modal
        containerClassName={modalClassName}
        heading={this.resolveProp(currentPage, 'title', title)}
        footer={this.resolveFooter(
          currentPage,
          navigationDisabled,
          saveDisabled,
          finishDisabled
        )}
        onRequestClose={this.close.bind(this)}
        dismissOnClickOutside={false}
        disableClose={false}
        isOpen={isOpen || false}
        size={this.props.size || 'medium'}
        {...otherProps}
      >
        {currentPage || <div />}
      </Modal>
    );
  }
}

const wizardPage = (props, propName, componentName) => {
  const component = componentName || 'undefined component';
  const child = props[propName];
  if (child.type.name !== WizardPage.name) {
    const found = typeof child.type === 'string' ? child.type : child.type.name;
    return new Error(
      `Child component of ${component} must be a ${
        WizardPage.name
      }: found ${found}`
    );
  }
  return null;
};

Wizard.contextTypes = {
  router: PropTypes.object
};

Wizard.propTypes = {
  actions: PropTypes.object.isRequired,
  children: PropTypes.arrayOf(wizardPage).isRequired,
  modalName: PropTypes.string.isRequired,
  modals: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  size: PropTypes.string,
  containerClassName: PropTypes.string,
  onFinish: PropTypes.func,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  onStepChange: PropTypes.func,
  finishLabel: PropTypes.string
};

export default injectIntl(Wizard);
